<template>
  <v-container fluid justify-start>

    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="playlists"
      :loading="isLoading"
      :search="search"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense dark flat :color="toolbarColor">
          <app-back-button v-bind:color="toolbarColorDark" />
          <!--<v-icon large :color="appColor_Genie" class="mr-2">perm_media</v-icon>-->
          <v-toolbar-title :class="titleClass">{{ title }}</v-toolbar-title>

          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog v-model="editDialog" max-width="400px" :persistent="true">
              <loading :active.sync="isSaving" :can-cancel="false" loader="dots" :color="appColor_Progress"></loading>
              <template v-slot:activator="{ on }">
                <v-btn slot="activator" :color="toolbarColorDark" class="mb-1 mt-0 mr-0 elevation-0" :disabled="disableAdd" v-on="on">{{ getExpression('Button', 'CreatePlaylist') }}</v-btn>
              </template>
              <v-toolbar dense dark flat :color="toolbarColorDark">
                <v-toolbar-title><v-icon class="pr-2">edit</v-icon> {{ formTitle }}</v-toolbar-title>
              </v-toolbar>
              <v-card>
                
                <v-card-text class="pa-0 pt-2 ma-0">
                  <v-container grid-list-md class="pt-3 pb-0">
                    <form>
                      <v-row no-gutters>
                        <v-col cols="12" xs="12">
                          <v-text-field
                            v-model="editedPlaylist.name"
                            :label="getExpression('Label', 'Name')"
                            v-validate="'required|max:50'"
                            :error-messages="errors.collect('name')"
                            data-vv-name="name"
                            clearable
                            maxlength="50"
                            ref="name"
                          />
                        </v-col>
                        
                        <v-col cols="12" xs="12">
                          <v-text-field
                            v-model="editedPlaylist.description"
                            :label="getExpression('Label', 'Description')"
                            clearable
                            maxlength="100"
                          />
                        </v-col>
                        <template v-if="editedPlaylist.id > 0">
                          <v-col cols="12" xs="6">
                            <v-switch
                              v-model="editedPlaylist.isActive"
                              :disabled="editedPlaylist.isDisableActivate"
                              :label="getExpression('Label', 'Activate')"
                              class="mt-1 pa-0"
                            />
                          </v-col>
                        </template>
                        <template v-if="editedPlaylist.isMissingMedia && editedPlaylist.id > 0">
                        <v-col cols="12" xs="6">
                          <p class="text-sm-left font-weight-black">{{ getExpression('Alert', 'PlaylistMissingMedia') }}</p>
                        </v-col>
                        </template>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>

                <v-card-actions class="pb-3">

                  <v-spacer />
                  
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="toolbarColorDark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>
                </v-card-actions>

              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">
          
          <!-- Kiosks Dropdown -->
          <v-col v-if="showKiosk" cols="12" sm="4">
            <kiosk-combobox
              v-model="kioskId"
              :options="kiosks"
              :label="getExpression('Label', 'Kiosk')"
              :placeholder="getExpression('Label', 'SelectKiosk')"
              :disabled="disableKiosk"
              class="mr-3"
            />
          </v-col>

          <!-- Search Text -->
          <v-col cols="12" :sm="searchColWidth">
            <v-text-field
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              :disabled="disableSearch"
              clearable
              >
            </v-text-field>
          </v-col>
          
        </v-row>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <span @click="edit(item)" style="white-space: nowrap">
            <span :style="appStyle_HyperLink">{{ item.name }}</span>
            <v-icon small class="ml-2" style="cursor: pointer">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.blobs="{ item }">
        <v-btn small dark @click="editDetail(item)" :color="appColor_GenieDark" height="36" class="rounded-0">{{ getExpression('Button', 'EditContent') }}</v-btn>
      </template>

      <template v-slot:item.isActive="{ item }">
        <span class="mr-4">
          <v-icon v-if="item.isActive" :color="appColor_Selected">done</v-icon>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left pl-0 pr-0"><app-date :date="item.createDate" :format="'MMM-DD-YYYY'" /></span>
      </template>

      <template v-slot:item.action="{ item }">
        <v-icon v-if="!item.isActive" @click="remove(item)"
          >delete
        </v-icon>
        <v-icon v-else color="grey lighten-1">delete</v-icon>
      </template>
    </v-data-table>

    <!-- Playlist Detail -->
    <v-dialog v-model="detailDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <playlist-detail
        v-model="editedPlaylist.playlistBlobs"
        v-bind:id="currentPlaylistId"
        v-bind:name="editedPlaylist.name"
        v-bind:ownerName="kioskName"
        v-bind:kioskId="kioskId"
        v-bind:defaultContainer="defaultContainer"
        v-bind:isActive="editedPlaylist.isActive"
        v-bind:isFromDashboard="isFromKioskDashboard"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-on:cancel="detailCancel"
        v-on:done="detailDone"
      />
    </v-dialog>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import PlaylistDetail from '@/components/PlaylistDetail'
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import KioskCombobox from '@/components/KioskCombobox'
import { mapGetters } from 'vuex'
import { Container, AppColor, AppStyle, UserRole, KioskTypeId } from '@/config.js'

export default {
  components: {
    PlaylistDetail,
    KioskCombobox,
    AppDate,
    AppBackButton
  },
  name: 'KioskPlaylistManager',
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {

      // colors
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Genie: AppColor.KioskTypeGenieLight,
      appColor_GenieDark: AppColor.KioskTypeGenie,

      // containers
      container_KioskPrefix: Container.KioskPrefix,

      // kiosk type ids
      kioskTypeId_Genie: KioskTypeId.Genie,

      // roles
      role_Manager: UserRole.Manager,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,
      
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      detailDialog: false,
      bulletinDialog: false,
      kioskId: null,
      kiosks: [],
      playlists: [],
      currentPlaylistId: null,
      editedIndex: -1,
      editedPlaylist: {
        id: -1,
        kioskId: null,
        name: null,
        description: null,
        isActive: null,
        isDisableActivate: false,
        isMissingMedia: true,
        createDate: null,
        playlistBlobs: []
      },
      defaultPlaylist: {
        id: -1,
        kioskId: null,
        name: null,
        description: null,
        isActive: null,
        isDisableActivate: false,
        isMissingMedia: true,
        createDate: null,
        playlistBlobs: []
      }
    }
  },
  computed: {
    ...mapGetters([
        'profile',
        'activeKioskItem',
        'isAdmin',
        'isKioskManager',
        'previousRoute',
        'locale']),
    title () {
      return !this.isAdmin
        ? this.kioskName
        : 'Community Playlists'
    },
    formTitle () {
      return this.editedPlaylist.id < 0 
        ? this.getExpression('FormTitle', 'NewPlaylist')
        : this.getExpression('FormTitle', 'EditPlaylist')
    },
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.kiosks.length === 0) {
        return this.getExpression('Alert', 'NoGenieKiosks')
      }

      if (this.playlists.length === 0 && this.kioskId !== null) {
        return this.getExpression('Alert', 'NoPlaylists')
      }

      return ''
    },
    headers () {
      return [
        {
          text: this.getExpression('Label', 'Name'),
          value: 'name',
          align: 'left',
          width: '20%'
        },
        {
          text: this.getExpression('Label', 'Description'),
          value: 'description',
          align: 'left',
          width: '35%'
        },
        {
          text: this.getExpression('Label', 'Media'),
          value: 'blobs',
          align: 'center',
          width: '5%',
          sortable: false
        },
        {
          text: this.getExpression('Label', 'Active'),
          value: 'isActive',
          align: 'center',
          width: '15%'
        },
        {
          text: this.getExpression('Label', 'Added'),
          value: 'createDate',
          align: 'left',
          width: '20%'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ]
    },
    dictionary () {
      return  {
        custom: {
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '50')
          },
          kiosk: {
            required: () => this.getExpression('Validation', 'KioskRequired')
          }
        }
      }
    },
    toolbarColor () {
      return this.appColor_Genie
    },
    toolbarColorDark () {
      return this.appColor_GenieDark
    },
    isFromKioskDashboard () {
      return this.previousRoute.indexOf('KioskDashboard') >= 0
        || (this.previousRoute.indexOf('Home') >= 0 && this.isKioskManager)
    },
    kioskName () {
      if (this.isFromKioskDashboard) return this.activeKioskItem.serialNumber

      const self = this
      const kiosks = self.kiosks.filter(value => {
        return value.id === self.kioskId
      })

      const kioskDesc = kiosks.length > 0
        ? kiosks[0].name
        : ''
        
      return kiosks.length > 0
        ? kioskDesc.substring(0, kioskDesc.indexOf(' '))
        : ''
    },
    activePlaylistId () {
      var playlists = this.playlists.filter(value => {
        return value.isActive
      })
      if (playlists.length > 0) {
        return playlists[0].id
      }
      return -1
    },
    defaultContainer () {
      if (this.isNullOrUndefined(this.activeKioskItem)) return null
      const self = this
      const kiosks = self.kiosks.filter(k => k.id === self.kioskId)
      return kiosks.length > 0
        ? kiosks[0].container
        : self.activeKioskItem.container
    },
    disableAdd () {
      const kioskId = this.kioskId ?? 0
      return this.isLoading || kioskId === 0
    },
    disableSearch () {
      const kioskId = this.kioskId ?? 0
      return this.isLoading || kioskId === 0
    },
    disableKiosk () {
      return this.kiosks.length === 0 || this.isLoading
    },
    showKiosk () {
      return this.isAdmin && !this.isFromKioskDashboard
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    },
    searchColWidth () {
      return this.showKiosk
        ? '8'
        : '12'
    }
  },
  methods: {
    loadData () {
      if (!this.isValidSession) return
      const self = this

      // dashboard mode
      if (self.isFromKioskDashboard) {
        self.kioskId = self.activeKioskItem.id

      // admin mode
      } else {
        self.isLoading = true
        appService.getKioskComboboxOptions(self.kioskTypeId_Genie).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            self.kiosks = data.options
            self.kioskId = 0
          }
          self.isLoading = false
        })
      }
    },

    loadPlaylists () {
      const self = this
      self.isLoading = true
      appService.getKioskPlaylists(self.kioskId).then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.playlists = data.playlists
        }
        self.isLoading = false
      })
    },

    edit (item) {
      this.editedIndex = this.playlists.indexOf(item)
      this.editedPlaylist = Object.assign({}, item)
      const isMissingMedia = (this.editedPlaylist.playlistBlobs.length === 0)

      this.editedPlaylist.isDisableActivate = this.editedPlaylist.id === this.activePlaylistId
        || isMissingMedia
      this.editedPlaylist.isMissingMedia = isMissingMedia
      this.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.playlists.indexOf(item)
      const name = self.playlists[index].name

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteCommunityPlaylist'),
        text: `<b>${name}</b>`,
        onConfirm: () => {
          self.isDeleting = true

          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.deleteKioskPlaylist(item.id, updateByUserId).then(data => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.playlists.splice(index, 1)
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          self.isSaving = true
          if (isEditMode) {
            var editedPlaylist = self.playlists[self.editedIndex]
            Object.assign(editedPlaylist, self.editedPlaylist)
            const playlistModel = self.getModel(editedPlaylist, isEditMode)
            appService.updateKioskPlaylist(editedPlaylist.id, playlistModel).then((data) => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                if (editedPlaylist.isActive) {
                  self.setActive(editedPlaylist.id)
                }
              }
              self.isSaving = false
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            const playlistModel = self.getModel(self.editedPlaylist, isEditMode)
            appService.addKioskPlaylist(playlistModel).then((data) => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.editedPlaylist.id = data.playlist.id
                self.editedPlaylist.kioskId = data.playlist.kioskId
                self.editedPlaylist.isActive = data.playlist.isActive
                self.editedPlaylist.createDate = data.playlist.createDate
                self.playlists.unshift(self.editedPlaylist)
                self.playlists[0].playlistBlobs = []
              }
              self.isSaving = false
              self.closeDialog()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (pl, isEditMode) {
      if (isEditMode) {
        return {
          kioskId: this.kioskId,
          name: pl.name,
          description: pl.description,
          isActive: pl.isActive,
          updateByUserId: this.profile.id,
          createDate: pl.createDate
        }
      } else {
        return {
          kioskId: this.kioskId,
          name: pl.name,
          description: pl.description,
          updateByUserId: this.profile.id
        }
      }
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedPlaylist()
      }, 300)
    },

    resetEditedPlaylist () {
      this.editedPlaylist = Object.assign({}, this.defaultPlaylist)
      this.editedIndex = -1
      this.$validator.reset()
    },

    editDetail (item) {
      const self = this
      self.editedIndex = self.playlists.indexOf(item)
      self.detailDialog = true
      setTimeout(() => {
        self.editedPlaylist = Object.assign({}, item)
        self.currentPlaylistId = self.editedPlaylist.id
      }, 200)
    },

    detailDone () {
      const self = this
      self.detailDialog = false

      self.playlists[self.editedIndex].playlistBlobs = []
      self.editedPlaylist.playlistBlobs.forEach(b => {
        self.playlists[self.editedIndex].playlistBlobs.push(b)
      })
      self.resetEditedPlaylist()
    },

    detailCancel () {
      this.detailDialog = false
      this.resetEditedPlaylist()
    },

    setActive (id) {
      this.playlists.forEach(pl => {
        pl.isActive = pl.id === id
      })
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    kioskId () {
      if (this.kioskId > 0) {
        this.loadPlaylists()
      } else this.playlists = []
    },

    isValidSession (val) {
      if (val === null) return
      this.loadData()
    },

    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          // set focus to name input in the edit dialog
          self.$refs.name.focus()
        }, 300)
      }
    }
  }
}
</script>
